/**
 * PublicAccessBanner — slim top-of-page banner driven by
 * GET /api/public/access-status.
 *
 * - status="live"          → renders nothing (no banner)
 * - status="private_beta"  → amber banner: "Private beta — features may change"
 * - status="testing"       → rose banner: "Internal testing — not for production use"
 * - status="maintenance"   → slate banner: "Scheduled maintenance"
 *
 * Mounted in App.js *above* the routed page content so it sits at the
 * very top of the viewport on every page. The component fetches once on
 * mount, fails silently if the network is unreachable, and respects the
 * user's dismissal for the rest of the session (sessionStorage).
 *
 * Stealth: never leaks the env-var name or any internal detail; only
 * the four allowed status values can flow through.
 */
import { useEffect, useState } from 'react';
import { X, AlertCircle, Beaker, Construction } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

const API = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'jtr_public_status_dismissed_v1';

const COPY = {
  private_beta: {
    en: { title: 'Private beta', body: 'Features may change without notice.' },
    ja: { title: 'プライベートベータ版', body: '機能は予告なく変更される場合があります。' },
    tone: 'amber', Icon: Beaker,
  },
  testing: {
    en: { title: 'Internal testing', body: 'This environment is not for production use.' },
    ja: { title: '内部テスト中', body: '本番運用向けではありません。' },
    tone: 'rose', Icon: AlertCircle,
  },
  maintenance: {
    en: { title: 'Scheduled maintenance', body: 'Some flows may be temporarily unavailable.' },
    ja: { title: 'メンテナンス実施中', body: '一部の機能が一時的にご利用いただけない場合があります。' },
    tone: 'slate', Icon: Construction,
  },
};

const TONE_CLASSES = {
  amber: 'bg-amber-50 text-amber-900 border-amber-200',
  rose:  'bg-rose-50 text-rose-900 border-rose-200',
  slate: 'bg-slate-100 text-slate-800 border-slate-200',
};

export default function PublicAccessBanner() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch { /* sessionStorage blocked → keep banner visible */ }

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/public/access-status`, { credentials: 'omit' });
        if (!r.ok) return;
        const j = await r.json();
        if (!cancelled) setStatus(j?.status || 'live');
      } catch { /* network/CORS → no banner */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status || status === 'live' || dismissed) return null;
  const entry = COPY[status];
  if (!entry) return null;
  const { Icon } = entry;
  const copy = ja ? entry.ja : entry.en;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
  };

  return (
    <div
      role="status"
      data-testid="public-access-banner"
      data-status={status}
      className={`w-full border-b ${TONE_CLASSES[entry.tone]}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3">
        <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.4} />
        <p className="flex-1 text-[12px] leading-snug">
          <span className="font-black uppercase tracking-wider text-[10px] mr-2">{copy.title}</span>
          <span>{copy.body}</span>
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={ja ? 'バナーを閉じる' : 'Dismiss banner'}
          data-testid="public-access-banner-dismiss"
          className="text-current opacity-70 hover:opacity-100 transition flex-shrink-0"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.4} />
        </button>
      </div>
    </div>
  );
}
